import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useNavigate, useParams } from 'react-router-dom'
import {
  retryCrossClientCounts,
  selectCrossClientCountsUnavailableFor,
  selectCrossClientResultBucketsFor,
  selectCrossClientResultExecutionLoading,
  selectCrossClientResultsExecution,
} from '../../../store/crossClient/crossClientResults'
import { CrmActionHostProvider } from '../Actions/CrmActionHostProvider'
import { CrossClientResultsInitialization } from './CrossClientResultsInitialization'
import { CrossClientResultsPoller } from './CrossClientResultsPoller'
import { CrossClientResultsViewReporter } from './CrossClientResultsViewReporter'
import { CrossClientActionsPoller } from '../Actions/CrossClientActionsPoller'
import { type CrossClientQueryExecution, type CrossClientQueryPhase, CrossClientQueryPhases } from '../../../models/CrossClient'
import { executionShowsResults } from '../../../store/crossClient/utils'
import { useDropJustStartedMark, useJustStarted } from './hooks'
import { CrossClientEmptyState } from '../Landing/CrossClientEmptyState'
import { Button } from '../../../components/Button/Button'
import crossClientLogo from '../../../assets/images/cross-client-logo.png'
import { CrossClientHeaderPortal } from './Header/CrossClientHeader'
import { Spinner } from '../../../components/Spinner/Spinner'
import { CrossClientQueryNotOwned } from '../NotOwned/CrossClientQueryNotOwned'
import { CcqRunProvider } from './Run/ccqRun'
import { CcqRunView } from './Run/CcqRunView'
import { CcqInitPage } from './Run/CcqInitState'
import './CrossClientResults.scss'

export const CrossClientResults = () => {
  const { executionId } = useParams()

  if (!executionId) {
    return <div>No query provided</div>
  }

  return (
    <>
      <CrossClientResultsInitialization executionId={executionId} />
      <CrossClientResultsPoller executionId={executionId} />
      <CrossClientResultsViewReporter executionId={executionId} />
      <CrossClientActionsPoller executionId={executionId} />
      <CrossClientResultsBody executionId={executionId} />
    </>
  )
}

interface CrossClientResultsBodyProps {
  executionId: string
}

const NO_CLIENT_PHASES: CrossClientQueryPhase[] = [
  CrossClientQueryPhases.NO_CLIENTS_WERE_FOUND,
  CrossClientQueryPhases.NO_CLIENTS_AFTER_FILTER_IDS,
  CrossClientQueryPhases.NO_CLIENTS_AFTER_FILTER_ROLE,
]

const CrossClientResultsFailed: React.FC = () => {
  const navigate = useNavigate()

  return (
    <div className='d-flex flex-column align-items-center justify-content-center gap-3 zocks-pt-7.5 pb-4'>
      <img src={crossClientLogo} alt='' className='cross-client-logo' />
      <h5 className='mb-0'>I couldn&apos;t get this query ready.</h5>
      <Button variant='primary' onClick={() => navigate('/cross-client')}>
        Start a new query
      </Button>
    </div>
  )
}

const CrossClientResultsNotReady: React.FC<{ execution: CrossClientQueryExecution }> = ({ execution }) =>
  NO_CLIENT_PHASES.includes(execution.phase) ? <CrossClientEmptyState message='No clients found for this query.' /> : <CrossClientResultsFailed />

const CrossClientResultsBody: React.FC<CrossClientResultsBodyProps> = ({ executionId }) => {
  const storedExecution = useAppSelector(selectCrossClientResultsExecution)
  // The route is reused across executions, and a failed or empty fetch leaves the previous one in the store.
  const execution = storedExecution?.id === executionId ? storedExecution : null
  const executionLoading = useAppSelector(selectCrossClientResultExecutionLoading)
  const bucketsFor = useAppSelector(selectCrossClientResultBucketsFor)
  const countsUnavailable = useAppSelector(selectCrossClientCountsUnavailableFor) === executionId
  const dispatch = useAppDispatch()
  const justStarted = useJustStarted()
  const startedQuery = justStarted?.executionId === executionId ? justStarted : undefined

  useDropJustStartedMark()

  // Wait for this execution's first counts, so the cards, the card that opens and the count animation start from real numbers.
  const listsResults = storedExecution?.id === executionId && executionShowsResults(storedExecution)
  const awaitsCounts = !executionLoading && listsResults && bucketsFor !== executionId
  // Also while it initializes: with polling stopped, nothing would show it start.
  const gaveUpOnCounts = !executionLoading && storedExecution?.id === executionId && bucketsFor !== executionId && countsUnavailable

  if (gaveUpOnCounts) {
    return (
      <div className='d-flex flex-column align-items-center justify-content-center gap-3 mt-10'>
        <div className='paragraph-medium medium'>Couldn&apos;t load the results.</div>
        <Button variant='primary' onClick={() => dispatch(retryCrossClientCounts())}>
          Try again
        </Button>
      </div>
    )
  }

  if (executionLoading || awaitsCounts) {
    if (startedQuery) {
      return <CcqInitPage question={startedQuery.question} criteria={startedQuery.criteria} shownSince={startedQuery.shownSince} />
    }
    return (
      <div className='d-flex flex-column align-items-center justify-content-center gap-3 mt-10'>
        <Spinner label='Pulling up your results…' />
        <div className='paragraph-medium medium'>Pulling up your results…</div>
      </div>
    )
  }

  if (!execution) {
    return <CrossClientQueryNotOwned />
  }

  // The run view owns everything from initializing on; the rest ended before there was anything to list.
  if (!executionShowsResults(execution) && execution.phase !== CrossClientQueryPhases.INITIALIZING) {
    return (
      <div className='truly-full-size-content d-flex flex-column align-items-center'>
        <CrossClientHeaderPortal execution={execution} />
        <CrossClientResultsNotReady execution={execution} />
      </div>
    )
  }

  return (
    <CrmActionHostProvider>
      <CcqRunProvider execution={execution}>
        <CcqRunView initShownSince={startedQuery?.shownSince} />
      </CcqRunProvider>
    </CrmActionHostProvider>
  )
}
